// Multi-select combobox with searchable filter + "add custom value" support.
// Used by Candidate Portal Dossier "Skills" section.
import { useEffect, useMemo, useRef, useState } from "react";
import { CaretDown, MagnifyingGlass, Plus, X } from "@phosphor-icons/react";

export default function MultiSelectWithCustom({
  options = [],          // array of strings
  value = [],            // array of strings
  onChange,
  placeholder = "Search or add…",
  disabled = false,
  testid,
  emptyMessage = "No items selected",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(() => value || [], [value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = options.filter((o) => !selected.includes(o));
    if (!q) return base.slice(0, 100);
    return base.filter((o) => o.toLowerCase().includes(q)).slice(0, 100);
  }, [options, selected, query]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const add = (item) => {
    const it = String(item).trim();
    if (!it) return;
    if (selected.includes(it)) return;
    onChange?.([...selected, it]);
    setQuery("");
    inputRef.current?.focus();
  };

  const remove = (item) => {
    onChange?.(selected.filter((s) => s !== item));
  };

  const canAddCustom = query.trim() &&
    !options.some((o) => o.toLowerCase() === query.trim().toLowerCase()) &&
    !selected.some((s) => s.toLowerCase() === query.trim().toLowerCase());

  return (
    <div ref={rootRef} className="relative" data-testid={testid}>
      {/* Chips + trigger */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => { if (!disabled) { setOpen(true); setTimeout(() => inputRef.current?.focus(), 30); } }}
        onKeyDown={(e) => { if (!disabled && (e.key === "Enter" || e.key === " ")) { setOpen(true); e.preventDefault(); } }}
        className={`w-full min-h-[42px] border border-slate-200 rounded-lg px-2 py-1.5 bg-white flex flex-wrap items-center gap-1 focus-within:ring-2 focus-within:ring-sky-500/30 focus-within:border-sky-500 transition ${disabled ? "bg-slate-50" : "hover:border-slate-300 cursor-text"}`}
        data-testid={testid ? `${testid}-trigger` : undefined}
      >
        {selected.length === 0 && !open && (
          <span className="text-sm text-slate-400 px-1.5 py-0.5">{emptyMessage}</span>
        )}
        {selected.map((s) => (
          <span key={s} className="inline-flex items-center gap-1 bg-sky-50 border border-sky-200 text-sky-800 rounded-full text-xs px-2.5 py-1"
                data-testid={testid ? `${testid}-chip` : undefined}>
            {s}
            {!disabled && (
              <button type="button" onClick={(e) => { e.stopPropagation(); remove(s); }}
                      className="text-sky-500 hover:text-red-600"
                      data-testid={testid ? `${testid}-chip-remove` : undefined}
                      title={`Remove ${s}`}>
                <X size={11} />
              </button>
            )}
          </span>
        ))}
        {!disabled && (
          <div className="flex-1 min-w-[120px] flex items-center gap-1">
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && query.trim()) {
                  const match = options.find((o) => o.toLowerCase() === query.trim().toLowerCase());
                  add(match || query.trim());
                  e.preventDefault();
                } else if (e.key === "Backspace" && !query && selected.length) {
                  remove(selected[selected.length - 1]);
                }
              }}
              placeholder={selected.length === 0 ? placeholder : ""}
              className="flex-1 text-sm outline-none py-1 bg-transparent"
              data-testid={testid ? `${testid}-input` : undefined}
            />
            <CaretDown size={12} className="text-slate-400" />
          </div>
        )}
      </div>

      {open && !disabled && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-hidden"
             data-testid={testid ? `${testid}-panel` : undefined}>
          {canAddCustom && (
            <button
              type="button"
              onClick={() => add(query.trim())}
              className="w-full text-left text-sm px-3 py-2 border-b border-slate-100 hover:bg-emerald-50 text-emerald-700 flex items-center gap-2"
              data-testid={testid ? `${testid}-add-custom` : undefined}
            >
              <Plus size={12} /> Add custom skill: <span className="font-medium">"{query.trim()}"</span>
            </button>
          )}
          <div className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 && !canAddCustom && (
              <div className="text-xs text-slate-400 px-3 py-2">
                {query.trim() ? "No matches — everything typed is already selected." : "Type to search skills…"}
              </div>
            )}
            {filtered.map((o) => (
              <button
                key={o}
                type="button"
                onClick={() => add(o)}
                className="w-full text-left text-sm px-3 py-1.5 hover:bg-sky-50 text-slate-700"
                data-testid={testid ? `${testid}-opt-${o.slice(0,20).replace(/\s+/g, "-")}` : undefined}
              >
                {o}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
